import { useMemo, useRef } from 'react'
import { appendRestartAttempt } from '../model'

/**
 * Applies the smart-restart `#restart-{n}` fragment so the player reliably
 * (re)loads a source even when the raw `playbackUrl` string is unchanged.
 *
 * pureya's `useSource` memoizes on the URL string only, so the player reloads a
 * source solely when that string changes. But a fresh URL is not always a fresh
 * string:
 *   - a retry re-fetches the SAME content (often a byte-identical URL);
 *   - two different channels can return the exact same (broken) `playbackUrl`
 *     and differ only in DRM.
 * Without a changing fragment the player would never load the new source — a
 * black screen with no error and no recovery.
 *
 * The fragment is bumped on every URL-fetch settle (`isUrlRequestPending`
 * going true → false), which is exactly "a new playback URL just arrived" for
 * every module (retry, content switch, same-URL switch). Crucially it is derived
 * in the SAME render as `playbackUrl`, so the fragment can never be applied to
 * the previous content's (stale) URL — which would otherwise trigger a stray
 * reload of the channel we just left. Effector-driven signals (content-switch or
 * URL-store events) fire before the rendered `playbackUrl` updates and cannot
 * offer that guarantee.
 */
export function usePlaybackUrlWithRestartAttempt(
  playbackUrl: string,
  isUrlRequestPending: boolean
): string {
  const generationRef = useRef(0)
  const wasPendingRef = useRef(isUrlRequestPending)

  if (wasPendingRef.current && !isUrlRequestPending) {
    generationRef.current += 1
  }
  wasPendingRef.current = isUrlRequestPending

  const attempt = generationRef.current

  return useMemo(
    () =>
      playbackUrl ? appendRestartAttempt(playbackUrl, attempt) : playbackUrl,
    [playbackUrl, attempt]
  )
}
